/**
 * Update / Deploy overlay — compact centered card shown while the client
 * downloads and applies a new build. Mirrors the logout modal's chrome
 * (dark panel, white border, scrim + blur) and uses the real Kinkygram
 * app icon. Four states: updating / progress / ready / minimal.
 *
 * Mounted once at app root; reads state from `update-overlay-state.ts`.
 */
import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useUpdateOverlayState, type ChangelogItem } from '@/lib/update-overlay-state';
import { getUserLanguage } from '@/lib/version-check';

// ── i18n ───────────────────────────────────────────────────────────────────
// L'overlay compare durante il reload (context React non disponibile), quindi
// la lingua viene letta da Telegram/browser via getUserLanguage().
type OverlayLang = 'it' | 'en' | 'es' | 'de' | 'pt' | 'fr';
const SUPPORTED: OverlayLang[] = ['it', 'en', 'es', 'de', 'pt', 'fr'];
const LOCALE: Record<OverlayLang, string> = {
  it: 'it-IT', en: 'en-US', es: 'es-ES', de: 'de-DE', pt: 'pt-PT', fr: 'fr-FR',
};

interface OverlayStrings {
  updating: string;
  updatingDesc: string;
  dontClose: string;
  updatingTitle: string;
  applyingLatest: string;
  download: string;
  almostReady: string;
  secRemaining: string; // suffisso, usato come `${n} ${secRemaining}`
  whatsNew: string;
  dontCloseRestart: string;
  allReady: string;
  updatedToLatest: string;
  goToApp: string;
  version: string;
  updatingShort: string;
  changelog: ChangelogItem[];
}

const DICT: Record<OverlayLang, OverlayStrings> = {
  it: {
    updating: 'Aggiornamento in corso',
    updatingDesc: "Stiamo applicando l'ultimo aggiornamento. Ci vorranno pochi secondi.",
    dontClose: "Non chiudere l'app",
    updatingTitle: 'Stiamo aggiornando Kinkygram',
    applyingLatest: "Applichiamo l'ultima versione.",
    download: 'Download',
    almostReady: 'Quasi pronto',
    secRemaining: 'sec rimanenti',
    whatsNew: "COSA C'È DI NUOVO",
    dontCloseRestart: "Non chiudere l'app · si riavvierà da sola",
    allReady: 'Tutto pronto',
    updatedToLatest: "Kinkygram è stato aggiornato all'ultima versione.",
    goToApp: "Vai all'app",
    version: 'VERSIONE',
    updatingShort: 'AGGIORNAMENTO',
    changelog: [
      { icon: '⚡', title: 'Speed dates più rapidi', description: 'Apertura instantanea, meno attese' },
      { icon: '🔮', title: 'Nuovo Kinky Test', description: 'Radar a 24 assi, match più precisi' },
      { icon: '🎟️', title: 'Eventi con TON', description: 'Paga in cripto, zero commissioni' },
    ],
  },
  en: {
    updating: 'Update in progress',
    updatingDesc: "We're applying the latest update. It'll only take a few seconds.",
    dontClose: "Don't close the app",
    updatingTitle: 'Updating Kinkygram',
    applyingLatest: 'Applying the latest version.',
    download: 'Download',
    almostReady: 'Almost ready',
    secRemaining: 'sec remaining',
    whatsNew: "WHAT'S NEW",
    dontCloseRestart: "Don't close the app · it will restart on its own",
    allReady: 'All set',
    updatedToLatest: 'Kinkygram has been updated to the latest version.',
    goToApp: 'Go to app',
    version: 'VERSION',
    updatingShort: 'UPDATING',
    changelog: [
      { icon: '⚡', title: 'Faster speed dates', description: 'Instant open, less waiting' },
      { icon: '🔮', title: 'New Kinky Test', description: '24-axis radar, sharper matches' },
      { icon: '🎟️', title: 'Events with TON', description: 'Pay in crypto, zero fees' },
    ],
  },
  es: {
    updating: 'Actualización en curso',
    updatingDesc: 'Estamos aplicando la última actualización. Solo tardará unos segundos.',
    dontClose: 'No cierres la app',
    updatingTitle: 'Actualizando Kinkygram',
    applyingLatest: 'Aplicando la última versión.',
    download: 'Descarga',
    almostReady: 'Casi listo',
    secRemaining: 'seg restantes',
    whatsNew: 'NOVEDADES',
    dontCloseRestart: 'No cierres la app · se reiniciará sola',
    allReady: 'Todo listo',
    updatedToLatest: 'Kinkygram se ha actualizado a la última versión.',
    goToApp: 'Ir a la app',
    version: 'VERSIÓN',
    updatingShort: 'ACTUALIZANDO',
    changelog: [
      { icon: '⚡', title: 'Speed dates más rápidos', description: 'Apertura instantánea, menos esperas' },
      { icon: '🔮', title: 'Nuevo Kinky Test', description: 'Radar de 24 ejes, matches más precisos' },
      { icon: '🎟️', title: 'Eventos con TON', description: 'Paga en cripto, cero comisiones' },
    ],
  },
  de: {
    updating: 'Update läuft',
    updatingDesc: 'Wir installieren das neueste Update. Es dauert nur wenige Sekunden.',
    dontClose: 'App nicht schließen',
    updatingTitle: 'Kinkygram wird aktualisiert',
    applyingLatest: 'Neueste Version wird angewendet.',
    download: 'Download',
    almostReady: 'Fast fertig',
    secRemaining: 'Sek. verbleibend',
    whatsNew: 'WAS IST NEU',
    dontCloseRestart: 'App nicht schließen · startet von selbst neu',
    allReady: 'Alles bereit',
    updatedToLatest: 'Kinkygram wurde auf die neueste Version aktualisiert.',
    goToApp: 'Zur App',
    version: 'VERSION',
    updatingShort: 'AKTUALISIERUNG',
    changelog: [
      { icon: '⚡', title: 'Schnellere Speed Dates', description: 'Sofort offen, weniger Warten' },
      { icon: '🔮', title: 'Neuer Kinky Test', description: '24-Achsen-Radar, präzisere Matches' },
      { icon: '🎟️', title: 'Events mit TON', description: 'Zahle in Krypto, keine Gebühren' },
    ],
  },
  pt: {
    updating: 'Atualização em andamento',
    updatingDesc: 'Estamos aplicando a última atualização. Vai levar só alguns segundos.',
    dontClose: 'Não feche o app',
    updatingTitle: 'Atualizando o Kinkygram',
    applyingLatest: 'Aplicando a última versão.',
    download: 'Download',
    almostReady: 'Quase pronto',
    secRemaining: 'seg restantes',
    whatsNew: 'NOVIDADES',
    dontCloseRestart: 'Não feche o app · ele reiniciará sozinho',
    allReady: 'Tudo pronto',
    updatedToLatest: 'O Kinkygram foi atualizado para a última versão.',
    goToApp: 'Ir para o app',
    version: 'VERSÃO',
    updatingShort: 'ATUALIZANDO',
    changelog: [
      { icon: '⚡', title: 'Speed dates mais rápidos', description: 'Abertura instantânea, menos espera' },
      { icon: '🔮', title: 'Novo Kinky Test', description: 'Radar de 24 eixos, matches mais precisos' },
      { icon: '🎟️', title: 'Eventos com TON', description: 'Pague em cripto, zero taxas' },
    ],
  },
  fr: {
    updating: 'Mise à jour en cours',
    updatingDesc: 'Nous appliquons la dernière mise à jour. Ça ne prendra que quelques secondes.',
    dontClose: "Ne fermez pas l'app",
    updatingTitle: 'Mise à jour de Kinkygram',
    applyingLatest: 'Application de la dernière version.',
    download: 'Téléchargement',
    almostReady: 'Presque prêt',
    secRemaining: 'sec restantes',
    whatsNew: 'QUOI DE NEUF',
    dontCloseRestart: "Ne fermez pas l'app · elle redémarrera toute seule",
    allReady: 'Tout est prêt',
    updatedToLatest: 'Kinkygram a été mis à jour vers la dernière version.',
    goToApp: "Aller à l'app",
    version: 'VERSION',
    updatingShort: 'MISE À JOUR',
    changelog: [
      { icon: '⚡', title: 'Speed dates plus rapides', description: 'Ouverture instantanée, moins d\'attente' },
      { icon: '🔮', title: 'Nouveau Kinky Test', description: 'Radar à 24 axes, matchs plus précis' },
      { icon: '🎟️', title: 'Événements avec TON', description: 'Payez en crypto, zéro frais' },
    ],
  },
};

function resolveLang(): OverlayLang {
  const raw = (getUserLanguage() || 'en').toLowerCase().substring(0, 2);
  return (SUPPORTED.includes(raw as OverlayLang) ? raw : 'en') as OverlayLang;
}

// ── Palette — aligned to the desktop logout modal (KG tokens) ──────────────
const KU = {
  scrim: 'rgba(6,8,12,0.6)',
  panel: '#141416',
  border: 'rgba(255,255,255,0.08)',
  borderHi: 'rgba(255,255,255,0.14)',
  text: '#f5f5f5',
  text2: '#cfcfd3',
  hint: '#808088',
  red: '#ec3942',
  pink: '#e94e8a',
  pinkHi: '#ff6ba1',
  purpleHi: '#c98ad9',
  blue: '#2565eb',
  green: '#22c55e',
  gold: '#f5c542',
} as const;

const APP_ICON = '/assets/images/brand/icon-512.webp';
const FONT = '-apple-system, "SF Pro", Inter, system-ui, sans-serif';

const KU_STYLE_ID = 'ku-overlay-keyframes';
const KU_KEYFRAMES = `
@keyframes kuPulse   { 0%,80%,100% { opacity: .25; transform: scale(.9); } 40% { opacity: 1; transform: scale(1.2); } }
@keyframes kuShimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
@keyframes kuSlide   { 0% { transform: translateX(-100%); } 50% { transform: translateX(0); } 100% { transform: translateX(100%); } }
@keyframes kuBreathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
`;

function useEnsureKeyframes() {
  useEffect(() => {
    if (document.getElementById(KU_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = KU_STYLE_ID;
    style.textContent = KU_KEYFRAMES;
    document.head.appendChild(style);
  }, []);
}

// ── Shared chrome ──────────────────────────────────────────────────────────
function AppIcon({ size = 48 }: { size?: number }) {
  return (
    <img
      src={APP_ICON}
      alt="Kinkygram"
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.24,
        flexShrink: 0,
        objectFit: 'cover',
        border: '1px solid rgba(255,255,255,0.10)',
        boxShadow: '0 8px 22px -8px rgba(0,0,0,0.6)',
      }}
    />
  );
}

function Card({ children, maxWidth = 380 }: { children: React.ReactNode; maxWidth?: number }) {
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%',
        maxWidth,
        background: KU.panel,
        border: `1px solid ${KU.borderHi}`,
        borderRadius: 16,
        padding: 22,
        boxShadow: '0 30px 70px -20px rgba(0,0,0,0.7)',
        color: KU.text,
      }}
      role="status"
      aria-live="polite"
    >
      {children}
    </div>
  );
}

function HeaderRow({
  title,
  subtitle,
  iconSize = 48,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  iconSize?: number;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
      <AppIcon size={iconSize} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 13, color: KU.text2, lineHeight: 1.45, marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
    </div>
  );
}

function ProgressBar({ pct }: { pct?: number }) {
  const indeterminate = pct == null;
  const fill = `linear-gradient(90deg, ${KU.pink}, ${KU.purpleHi})`;
  return (
    <div style={{ height: 6, borderRadius: 99, background: 'rgba(255,255,255,0.06)', overflow: 'hidden', position: 'relative' }}>
      {indeterminate ? (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: '40%',
            borderRadius: 99,
            background: fill,
            animation: 'kuSlide 1.6s ease-in-out infinite',
          }}
        />
      ) : (
        <div
          style={{
            width: `${Math.max(0, Math.min(1, pct)) * 100}%`,
            height: '100%',
            borderRadius: 99,
            background: fill,
            position: 'relative',
            overflow: 'hidden',
            transition: 'width 0.3s ease-out',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)',
              animation: 'kuShimmer 1.6s linear infinite',
              transform: 'translateX(-100%)',
            }}
          />
        </div>
      )}
    </div>
  );
}

function Footer({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, color: KU.hint, letterSpacing: '0.04em', textAlign: 'center', marginTop: 16 }}>{children}</div>;
}

// ── ① Updating — indeterminate ────────────────────────────────────────────
function ScreenUpdating({ t }: { t: OverlayStrings }) {
  return (
    <Card>
      <HeaderRow title={t.updating} subtitle={t.updatingDesc} />
      <ProgressBar />
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 8 }}>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{ width: 7, height: 7, borderRadius: 99, background: KU.purpleHi, animation: `kuPulse 1.4s ease-in-out ${i * 0.2}s infinite` }}
          />
        ))}
      </div>
      <Footer>{t.dontClose}</Footer>
    </Card>
  );
}

// ── ② Progress + changelog ────────────────────────────────────────────────
function ScreenProgress({
  t,
  percent,
  bytesTotal,
  etaSec,
  changelog,
  versionFrom,
  versionTo,
}: {
  t: OverlayStrings;
  percent: number;
  bytesTotal?: number;
  etaSec?: number;
  changelog?: ChangelogItem[];
  versionFrom?: string;
  versionTo?: string;
}) {
  const items = (changelog && changelog.length > 0 ? changelog : t.changelog).slice(0, 3);
  const pct = Math.max(0, Math.min(1, percent));
  const sizeLabel = bytesTotal ? `${(bytesTotal / 1024 / 1024).toFixed(1)} MB` : '12.4 MB';
  const versionLabel =
    versionFrom && versionTo ? `v ${versionFrom} → v ${versionTo}` : versionTo ? `v ${versionTo}` : null;
  const etaLabel = typeof etaSec === 'number'
    ? `${Math.max(0, Math.round(etaSec))} ${t.secRemaining}`
    : `4 ${t.secRemaining}`;

  return (
    <Card maxWidth={400}>
      <HeaderRow
        title={t.updatingTitle}
        subtitle={
          versionLabel ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 5, height: 5, borderRadius: 99, background: KU.gold, boxShadow: `0 0 6px ${KU.gold}` }} />
              <span style={{ color: KU.gold, fontWeight: 700, letterSpacing: '0.04em' }}>{versionLabel}</span>
            </span>
          ) : (
            t.applyingLatest
          )
        }
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: KU.hint, letterSpacing: '0.06em', fontWeight: 600, textTransform: 'uppercase' }}>
          {t.download} · {sizeLabel}
        </span>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{Math.round(pct * 100)}%</span>
      </div>
      <ProgressBar pct={pct} />
      <div style={{ marginTop: 8, fontSize: 11, color: KU.hint, display: 'flex', justifyContent: 'space-between' }}>
        <span>Quasi pronto</span>
        <span>{etaLabel}</span>
      </div>

      <div style={{ marginTop: 18, paddingTop: 4 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: KU.hint, letterSpacing: '0.16em', marginBottom: 8 }}>
          ✦ {t.whatsNew}
        </div>
        {items.map((item, i) => (
          <div
            key={i}
            style={{ display: 'flex', gap: 12, padding: '9px 0', borderBottom: i < items.length - 1 ? `1px solid ${KU.border}` : 'none' }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: 'rgba(255,255,255,0.06)',
                border: `1px solid ${KU.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: 15,
              }}
            >
              {item.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{item.title}</div>
              <div style={{ fontSize: 11, color: KU.hint, marginTop: 2 }}>{item.description}</div>
            </div>
          </div>
        ))}
      </div>

      <Footer>{t.dontCloseRestart}</Footer>
    </Card>
  );
}

// ── ③ Ready — success ─────────────────────────────────────────────────────
function ScreenReady({ t, lang, version, onContinue }: { t: OverlayStrings; lang: OverlayLang; version?: string; onContinue?: () => void }) {
  const versionLabel = useMemo(() => {
    const v = `${t.version} ${version || '1.0'}`;
    const d = new Date();
    const month = d.toLocaleString(LOCALE[lang], { month: 'short' }).toUpperCase().replace('.', '');
    return `${v} · ${month} ${d.getFullYear()}`;
  }, [version, t, lang]);

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <AppIcon size={48} />
          <span
            style={{
              position: 'absolute',
              right: -4,
              bottom: -4,
              width: 22,
              height: 22,
              borderRadius: 99,
              background: KU.green,
              border: `2px solid ${KU.panel}`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 13l4 4L19 7" />
            </svg>
          </span>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{t.allReady}</div>
          <div style={{ fontSize: 13, color: KU.text2, lineHeight: 1.45, marginTop: 2 }}>
            {t.updatedToLatest}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 10, fontWeight: 700, color: KU.gold, letterSpacing: '0.16em', marginBottom: 18 }}>{versionLabel}</div>

      <button
        type="button"
        onClick={onContinue}
        style={{
          width: '100%',
          height: 44,
          borderRadius: 12,
          border: 'none',
          background: `linear-gradient(135deg, ${KU.pinkHi}, ${KU.purpleHi})`,
          color: '#fff',
          fontSize: 15,
          fontWeight: 700,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          fontFamily: FONT,
          boxShadow: `0 12px 30px -10px ${KU.pinkHi}`,
        }}
      >
        {t.goToApp}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </button>
    </Card>
  );
}

// ── ④ Minimal — silent deploy ─────────────────────────────────────────────
function ScreenMinimal({ t }: { t: OverlayStrings }) {
  return (
    <Card maxWidth={300}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '6px 0 4px' }}>
        <div style={{ animation: 'kuBreathe 2.4s ease-in-out infinite' }}>
          <AppIcon size={64} />
        </div>
        <div style={{ marginTop: 18, marginBottom: 16, fontSize: 11, color: KU.hint, letterSpacing: '0.28em', fontWeight: 600 }}>
          {t.updatingShort}
        </div>
        <div style={{ width: '100%' }}>
          <ProgressBar />
        </div>
      </div>
    </Card>
  );
}

// ── Manager / portal ──────────────────────────────────────────────────────
export function UpdateOverlay() {
  const state = useUpdateOverlayState();
  useEnsureKeyframes();

  if (state.kind === 'idle') return null;

  const lang = resolveLang();
  const t = DICT[lang];

  let content: JSX.Element | null = null;
  switch (state.kind) {
    case 'updating':
      content = <ScreenUpdating t={t} />;
      break;
    case 'progress':
      content = (
        <ScreenProgress
          t={t}
          percent={state.percent}
          bytesTotal={state.bytesTotal}
          etaSec={state.etaSec}
          changelog={state.changelog}
          versionFrom={state.versionFrom}
          versionTo={state.versionTo}
        />
      );
      break;
    case 'minimal':
      content = <ScreenMinimal t={t} />;
      break;
    case 'ready':
      content = <ScreenReady t={t} lang={lang} version={state.version} onContinue={state.onContinue} />;
      break;
  }

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999999,
        background: KU.scrim,
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: FONT,
      }}
    >
      {content}
    </div>,
    document.body
  );
}

export default UpdateOverlay;
